/**
 * What the studio calls a work order's state, and whose move it is.
 *
 * Two surfaces read this — the ledger on a client's page and the work-order screen —
 * and they used to be one surface, so the map lived on `WorkOrdersSection`. Session
 * 24 gave the second one a route, and a second copy of eight words is how "In
 * progress" becomes "In-progress" on one screen and not the other.
 *
 * It is **not** `pages/client/requests/words.ts` `STATUS_WORD`, and the difference is
 * not carelessness: that one is lower-case because it fills a `.step` in a lifecycle
 * strip the client reads left to right, and this one is a `Pill` and a page subtitle.
 * Same enum, two renderings, and DESIGN.md P5 is the rule that lets a surface decide
 * how loudly it says a thing.
 *
 * Nothing here is a fact. Every word is a label over an enum `packages/shared` owns.
 */

import type { WorkOrderView } from '@fareast/shared'

/**
 * `workOrderMachine`'s own eight states, as a person says them.
 *
 * Written out rather than de-underscored, because "In progress" is two words and
 * `in_progress` is one — and because a state that gains a name should fail a type
 * check here rather than print `some_new_state`.
 */
export const STATUS_WORD: Readonly<Record<WorkOrderView['status'], string>> = {
  draft: 'Draft',
  proposed: 'Proposed',
  approved: 'Approved',
  in_progress: 'In progress',
  delivered: 'Delivered',
  billed: 'Billed',
  declined: 'Declined',
  cancelled: 'Cancelled',
}

/**
 * A project or an extra, said in words — `kind` is the column and this is the noun.
 *
 * "A project and an extra are one table and `kind` says which" (`CLAUDE.md`), and a
 * scope change is an extra with a parent. The caller passes the parent because the
 * row itself cannot tell a bare extra from a scope change without it.
 */
export function kindWord(row: Pick<WorkOrderView, 'kind' | 'parent_ref'>): string {
  if (row.kind === 'project') return 'Project'
  return row.parent_ref === null ? 'Extra' : 'Scope change'
}

/**
 * Whose move it is, in one sentence — the page's own conclusion (DESIGN.md P1).
 *
 * Ordered by what stops the work: a terminal state, then a quote that has run out,
 * then whoever is being waited on. Nothing here derives a figure; every branch is a
 * stored field read once.
 *
 * **"Waiting on them" for a proposal is the whole point of the screen.** `SERVICES.md`
 * §Projects — "Client approval in the portal is the contract event" — so the studio's
 * move on a `proposed` row is to chase it or to withdraw it, never to approve it.
 */
export function nextMoveSentence(row: WorkOrderView): string {
  switch (row.status) {
    case 'cancelled':
      return 'This was cancelled. Nothing further happens and nothing reaches an invoice.'
    case 'declined':
      return row.decline_note === null
        ? 'They said no. A new request is the way back, not this one.'
        : `They said no: ${row.decline_note}`
    case 'billed':
      return 'Delivered and billed. There is nothing left to do here.'
    case 'delivered':
      return row.not_billed
        ? 'Delivered, and the charge was waived — it reaches the invoice at zero.'
        : 'Delivered. The billing run collects it on the client’s billing day.'
    case 'in_progress':
      return 'The work is running. Deliver a milestone to invoice it, or deliver the whole thing.'
    case 'approved':
      return 'They approved it and nobody has started. Starting is what the client sees next.'
    case 'proposed':
      return row.expired
        ? 'The quote has run out, so they cannot approve it. Re-propose it to start a new thirty days.'
        : 'It is with the client, and approving it is the contract event. Nudge them, or take it back to draft to change the number.'
    case 'draft':
      return row.total_amount === null
        ? 'Nobody has priced it yet, and nothing has been sent. Price it, then send it.'
        : 'Priced and not sent. The client has seen none of this until it goes.'
  }
}
